// Pure helpers for the drag-move confirmation pipeline. No DOM, no React.
// Turns drag state into a concrete MovePlan and the confirmation copy.
// Reuses the dnd.js path math so destinations match the executed move exactly.

import { composeDropTarget, expandFolderMoves } from './dnd.js';

/**
 * Build the concrete plan for a drag-drop move.
 *
 * @param {object} args
 * @param {'file'|'folder'} args.sourceType
 * @param {string|null} args.sourceFolder  grabbed folder path (folder drags)
 * @param {string[]} args.draggedPaths     synchronously-captured file set (file drags)
 * @param {string} args.targetFolder       '' for root
 * @param {Array} args.tree                tree snapshot
 * @returns {{kind:'noop'}
 *   | {kind:'file', dest:string, moves:Array}
 *   | {kind:'multi', dest:string, sources:string[], moves:Array}
 *   | {kind:'folder', sourceFolder:string, fileCount:number, dest:string, moves:Array}}
 */
export function buildMovePlan({ sourceType, sourceFolder, draggedPaths, targetFolder, tree }) {
  if (sourceType === 'folder') {
    if (!sourceFolder) return { kind: 'noop' };
    const moves = expandFolderMoves({ tree, sourceFolder, targetFolder });
    if (moves.length === 0) return { kind: 'noop' };
    return {
      kind: 'folder',
      sourceFolder,
      fileCount: moves.length,
      dest: targetFolder,
      moves,
    };
  }

  const paths = draggedPaths || [];
  const moves = paths
    .map((src) => ({ source: src, destination: composeDropTarget({ sourcePath: src, targetFolder }) }))
    .filter((m) => m.source !== m.destination);
  if (moves.length === 0) return { kind: 'noop' };
  if (moves.length === 1) {
    return { kind: 'file', dest: targetFolder, moves };
  }
  return {
    kind: 'multi',
    dest: targetFolder,
    sources: moves.map((m) => m.source),
    moves,
  };
}

const SOURCE_CAP = 8;

function destLabel(dest) {
  return dest === '' ? "'root'" : `'${dest}/'`;
}

/**
 * Render confirmation copy from a plan.
 * @returns {{title:string, sources:string[]|null, capped:{shown:number,more:number}|null}}
 */
export function moveConfirmCopy(plan) {
  if (plan.kind === 'file') {
    const src = plan.moves[0].source;
    return { title: `Move '${src}' to ${destLabel(plan.dest)}?`, sources: null, capped: null };
  }
  if (plan.kind === 'multi') {
    const n = plan.sources.length;
    const noun = n === 1 ? 'item' : 'items';
    const shown = plan.sources.slice(0, SOURCE_CAP);
    const more = n - shown.length;
    return {
      title: `Move ${n} ${noun} to ${destLabel(plan.dest)}?`,
      sources: shown,
      capped: more > 0 ? { shown: shown.length, more } : null,
    };
  }
  if (plan.kind === 'folder') {
    const noun = plan.fileCount === 1 ? 'file' : 'files';
    return {
      title: `Move folder '${plan.sourceFolder}/' (${plan.fileCount} ${noun}) into ${destLabel(plan.dest)}?`,
      sources: null,
      capped: null,
    };
  }
  return { title: '', sources: null, capped: null };
}
